import { memo, useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Layout, LayoutContent } from '@astryxdesign/core/Layout'
import { Stack } from '@astryxdesign/core/Stack'
import { Card } from '@astryxdesign/core/Card'
import { Heading } from '@astryxdesign/core/Heading'
import { Text } from '@astryxdesign/core/Text'
import { Divider } from '@astryxdesign/core/Divider'
import { EmptyState } from '@astryxdesign/core/EmptyState'
import { Spinner } from '@astryxdesign/core/Spinner'
import { StatusDot } from '@astryxdesign/core/StatusDot'
import { List, ListItem } from '@astryxdesign/core/List'
import { Table, TableHeader, TableBody, TableRow, TableCell, TableHeaderCell } from '@astryxdesign/core/Table'
import { TextInput } from '@astryxdesign/core/TextInput'
import { Button } from '@astryxdesign/core/Button'
import { Banner } from '@astryxdesign/core/Banner'
import StoreBadges from '../components/StoreBadges'
import {
    SIDE_US, SIDE_THEM, formatLabel, teamName, pointHeadline, pointDetails, scoreLine, sinceText,
} from './trackedMatchLiveText'

// The live page of a tracked match at /match/{token}, opened from a link the
// person tracking it sent - to family, to a club's group chat, to a coach who
// could not come. No account and no app: the token in the URL is the whole
// credential, and the server answers with names and scores only.
//
// The score on top, the points underneath, the newest first. Nothing is
// computed here: the server replays the match and sends the score after every
// point, so this page cannot disagree with the apps about a tie-break.
//
// It polls rather than listening. The site's only socket is per user, and the
// person reading this has no user. Every five seconds while the match is on,
// paused while the tab is hidden, and not at all once it is over.
//
// Same frame as the public profile, the other page of this site somebody
// reaches from a link without signing in.
const APP_STORE_URL = "https://apps.apple.com/app/padelr/id6657992480"
const PLAY_STORE_URL = "https://play.google.com/apps/testing/com.padel.day.padelr"

const POLL_MS = 5000

// A code typed once is remembered for this tab, so a reload does not ask again.
// sessionStorage rather than the URL: a link copied from the address bar must
// not carry the code along with it.
const codeKey = token => `padelr.match.${token}.code`

function rememberedCode(token) {
    try {
        return window.sessionStorage.getItem(codeKey(token)) || ''
    } catch (e) {
        return ''
    }
}

function rememberCode(token, code) {
    try {
        window.sessionStorage.setItem(codeKey(token), code)
    } catch (e) {
        // Private browsing can refuse storage. The page still works; a reload
        // asks for the code again.
    }
}

function AppCta() {
    return (
        <Stack gap={3} hAlign="center">
            <Text type="supporting">Suivez vos matchs avec Padelr</Text>
            <StoreBadges appStoreURL={APP_STORE_URL} playStoreURL={PLAY_STORE_URL} />
        </Stack>
    )
}

function Frame({ children }) {
    return (
        <Layout padding={6} contentWidth={560} height="auto">
            <LayoutContent padding={6}>
                {/* Not centred: a centred stack shrinks the point list to the
                    width of its text. The call to action centres itself. */}
                <Stack gap={8}>
                    {children}
                    <AppCta />
                </Stack>
            </LayoutContent>
        </Layout>
    )
}

// The name cell of one side: who they are, and whether they serve or won.
function SideCell({ match, board, side }) {
    const serving = !board.finished && board.serving === side
    const won = board.finished && board.winning_side === side

    return (
        <Stack direction="horizontal" gap={2} vAlign="center">
            {serving && <StatusDot variant="accent" label="Au service" tooltip="Au service" />}
            {won && <StatusDot variant="success" label="Vainqueur" tooltip="Vainqueur" />}
            <Text weight={won ? 'bold' : 'medium'}>{teamName(match, side)}</Text>
        </Stack>
    )
}

function Scoreboard({ match, board }) {
    const sets = board.sets || []
    const live = !board.finished
    const showGames = live && !board.in_super_tiebreak

    let pointsHeader = 'Points'
    if (board.in_super_tiebreak) pointsHeader = 'Super tie-break'
    else if (board.in_tiebreak) pointsHeader = 'Tie-break'

    const setCell = (set, side) => {
        if (set.detail_unknown) return '?'
        const games = side === SIDE_US ? set.us : set.them
        return String(games)
    }

    const row = side => (
        <TableRow key={side}>
            <TableCell><SideCell match={match} board={board} side={side} /></TableCell>
            {sets.map((set, i) => (
                <TableCell key={i}>
                    <Text hasTabularNumbers weight={set.winner === side ? 'bold' : 'normal'}>
                        {setCell(set, side)}
                    </Text>
                </TableCell>
            ))}
            {showGames && (
                <TableCell>
                    <Text hasTabularNumbers>{side === SIDE_US ? board.games_us : board.games_them}</Text>
                </TableCell>
            )}
            {live && (
                <TableCell>
                    <Text hasTabularNumbers weight="bold" color="accent">
                        {side === SIDE_US ? board.display_us : board.display_them}
                    </Text>
                </TableCell>
            )}
        </TableRow>
    )

    return (
        <Table density="balanced">
            <TableHeader>
                <TableRow>
                    <TableHeaderCell>Équipe</TableHeaderCell>
                    {sets.map((set, i) => (
                        <TableHeaderCell key={i}>{`Set ${i + 1}`}</TableHeaderCell>
                    ))}
                    {showGames && <TableHeaderCell>Jeux</TableHeaderCell>}
                    {live && <TableHeaderCell>{pointsHeader}</TableHeaderCell>}
                </TableRow>
            </TableHeader>
            <TableBody>
                {row(SIDE_US)}
                {row(SIDE_THEM)}
            </TableBody>
        </Table>
    )
}

// Finished is read off the scoreboard, which is replayed from the points on
// every read, rather than off the stored summary beside it.
function Status({ match, board, now, offline }) {
    if (board.finished) {
        return (
            <Stack direction="horizontal" gap={2} vAlign="center">
                <StatusDot variant="neutral" label="Terminé" />
                <Text type="supporting">Terminé</Text>
            </Stack>
        )
    }

    return (
        <Stack direction="horizontal" gap={2} vAlign="center">
            <StatusDot variant={offline ? 'warning' : 'success'} label="En direct" isPulsing={!offline} />
            <Text type="supporting">
                {`En direct · mis à jour ${sinceText(match.updated_at, now)}`}
            </Text>
        </Stack>
    )
}

// Memoised: the status line re-renders every second to move "il y a 8 s" on,
// and six hundred rows have no reason to follow it.
const PointList = memo(function PointList({ match, points }) {
    if (points.length === 0) {
        return (
            <EmptyState
                title="Le match n'a pas encore commencé"
                description="Les points apparaîtront ici dès qu'ils seront joués."
            />
        )
    }

    return (
        <List hasDividers density="compact" header={<Heading level={2}>Points</Heading>}>
            {points.map((point, i) => {
                const details = pointDetails(match, point)
                const previous = i + 1 < points.length ? points[i + 1] : null

                return (
                    <ListItem
                        key={points.length - i}
                        label={pointHeadline(match, point, previous)}
                        description={details || undefined}
                        endContent={<Text hasTabularNumbers type="supporting">{scoreLine(point)}</Text>}
                    />
                )
            })}
        </List>
    )
})

function CodeForm({ wrong, onSubmit }) {
    const [code, setCode] = useState('')
    const submit = () => { if (code.trim().length > 0) onSubmit(code.trim()) }

    return (
        <Card width="100%">
            <Stack gap={4}>
                <Stack gap={1}>
                    <Heading level={1}>Match protégé</Heading>
                    <Text type="supporting">
                        Saisissez le code que la personne qui suit le match vous a donné.
                    </Text>
                </Stack>
                <TextInput
                    label="Code"
                    value={code}
                    onChange={setCode}
                    onEnter={submit}
                    autoComplete="off"
                    hasAutoFocus
                    status={wrong ? { type: 'error', message: 'Code incorrect' } : undefined}
                />
                <Button variant="primary" label="Voir le match" onClick={submit} />
            </Stack>
        </Card>
    )
}

export function TrackedMatchLiveView({ token }) {
    // loading | code | live | gone
    const [state, setState] = useState({ phase: 'loading' })
    // The attempt is part of the key so that submitting the same wrong code
    // twice still asks again.
    const [code, setCode] = useState(() => ({ value: rememberedCode(token), attempt: 0 }))
    const [offline, setOffline] = useState(false)
    const [now, setNow] = useState(() => Date.now())
    const timer = useRef(null)

    // Returns whether there is anything left to poll for.
    const load = useCallback(async () => {
        let response
        try {
            response = await fetch('/api/v1/trackedmatch/shared', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token, code: code.value }),
            })
        } catch (e) {
            // The network, not the link: keep what is on screen and say so.
            setOffline(true)
            return true
        }

        // Every refusal reaches the browser as a 403, and the only one this
        // call makes is "this link leads nowhere". Anything else non-2xx is
        // the server or the proxy having a moment.
        if (response.status === 403) {
            setState({ phase: 'gone' })
            return false
        }

        if (!response.ok) {
            setOffline(true)
            return true
        }

        const result = await response.json()
        setOffline(false)

        switch (result.access) {
            case 'SHARED_TRACKED_MATCH_ACCESS_GRANTED':
                if (code.value) rememberCode(token, code.value)
                setState({
                    phase: 'live',
                    match: result.match,
                    board: result.scoreboard,
                    points: result.points || [],
                })
                return !result.scoreboard.finished
            case 'SHARED_TRACKED_MATCH_ACCESS_CODE_WRONG':
                setState({ phase: 'code', wrong: true })
                return false
            default:
                setState({ phase: 'code', wrong: false })
                return false
        }
    }, [token, code])

    // Poll while the match is on and the tab is visible. Polling stops for
    // good once there is nothing left to change: the match is over, the link
    // is dead, or the page is waiting for a code - a new code starts it again.
    useEffect(() => {
        let stopped = false
        let paused = false

        const tick = async () => {
            if (stopped) return

            if (document.visibilityState !== 'visible') {
                paused = true
                return
            }

            const more = await load()
            if (more && !stopped) timer.current = setTimeout(tick, POLL_MS)
        }

        const onVisible = () => {
            if (paused && document.visibilityState === 'visible') {
                paused = false
                tick()
            }
        }

        tick()
        document.addEventListener('visibilitychange', onVisible)

        return () => {
            stopped = true
            clearTimeout(timer.current)
            document.removeEventListener('visibilitychange', onVisible)
        }
    }, [load])

    const phase = state.phase
    const finished = phase === 'live' && state.board.finished

    // "il y a 8 s" moves on between polls.
    useEffect(() => {
        if (phase !== 'live' || finished) return undefined
        const id = setInterval(() => setNow(Date.now()), 1000)
        return () => clearInterval(id)
    }, [phase, finished])

    if (phase === 'loading') {
        return (
            <Frame>
                <Stack gap={4} hAlign="center" paddingBlock={10}>
                    <Spinner />
                </Stack>
            </Frame>
        )
    }

    if (phase === 'gone') {
        return (
            <Frame>
                <EmptyState
                    headingLevel={1}
                    title="Ce lien de suivi n'est plus actif"
                    description="La personne qui suit le match a arrêté le partage, ou a envoyé un nouveau lien."
                />
            </Frame>
        )
    }

    if (phase === 'code') {
        return (
            <Frame>
                <CodeForm
                    wrong={state.wrong}
                    onSubmit={value => setCode(previous => ({ value, attempt: previous.attempt + 1 }))}
                />
            </Frame>
        )
    }

    const { match, board, points } = state
    const subtitle = [match.label, formatLabel(match.format)].filter(s => s && s.length > 0).join(' · ')

    return (
        <Frame>
            <Card width="100%">
                <Stack gap={4}>
                    <Stack gap={1}>
                        <Heading level={1}>
                            {`${teamName(match, SIDE_US)} – ${teamName(match, SIDE_THEM)}`}
                        </Heading>
                        {subtitle && <Text type="supporting">{subtitle}</Text>}
                    </Stack>
                    <Scoreboard match={match} board={board} />
                    <Divider />
                    <Status match={match} board={board} now={now} offline={offline} />
                </Stack>
            </Card>

            {offline && (
                <Banner
                    status="warning"
                    title="Connexion perdue"
                    description="Le score affiché est peut-être en retard. Nouvel essai dans quelques secondes."
                />
            )}

            <PointList match={match} points={points} />
        </Frame>
    )
}

export default TrackedMatchLiveView

TrackedMatchLiveView.propTypes = {
    token: PropTypes.string.isRequired,
}
